import { ComponentProps, FC } from 'react';

import CreatableSelect from 'react-select/creatable';
import cn from 'classnames';
import { MdClear } from 'react-icons/md';
import { validateEmailAddress } from 'utils/helpers';
import { Avatar } from 'ui/components/img';
import { components } from 'react-select';
import type {
  ClearIndicatorProps,
  DropdownIndicatorProps,
  IndicatorsContainerProps,
} from 'react-select';

const Option = (props: any) => {
  const { data } = props;

  if (!data.metadata) {
    return (
      <div
        {...props.innerProps}
        role="button"
        className={cn(
          'hover:bg-[#F2FAFF] break-all text-center px-4 py-[10px]',
          props.isFocused && 'bg-[#F2FAFF]',
        )}
      >
        Create {data.value}...
      </div>
    );
  }
  return (
    <div
      {...props.innerProps}
      className={cn(
        'flex gap-[14px] items-center',
        'px-4 py-[10px] hover:bg-[#F2FAFF]',
        props.isFocused && 'bg-[#F2FAFF]',
      )}
    >
      <Avatar className="sm" user={data.metadata} />
      <div>
        <div className="text-sm leading-5 tracking-wider text-secondary-base">
          {`${data?.metadata?.firstName} ${data?.metadata?.lastName ?? ''}`}
        </div>
        <div className="text-[#718FA5] text-xs leading-4 tracking-wider">
          {data?.metadata?.emailAddress}
        </div>
      </div>
    </div>
  );
};

const MultiValueRemove = ({ innerProps, ...rest }: any) => {
  const { isDisabled } = rest.selectProps;
  return (
    <MdClear
      {...innerProps}
      role="button"
      className={cn(
        isDisabled && 'hidden',
        'w-4 h-4 duration-100 hover:text-danger-base',
      )}
    />
  );
};

type EmailAddressMultiSelectProps = Omit<
  ComponentProps<CreatableSelect>,
  'onChange'
> & {
  validator?: (email: string) => boolean;
  onChange: (name: string, val: any) => void;
};

const EmailAddressMultiselect: FC<EmailAddressMultiSelectProps> = ({
  isClearable,
  onChange,
  placeholder,
  onBlur,
  options,
  defaultValue,
  value,
  allowCreateWhileLoading,
  isDisabled,
  onInputChange,
  name,
  isLoading,
  validator = validateEmailAddress,
  onCreateOption,
}) => {
  const handleChange = (val: any) => {
    onChange?.(name as string, val);
  };

  const noOptionsMessage = ({ inputValue }: any) => {
    if (!inputValue) {
      return 'Type or choose an email address';
    }
    return `No Results for ${inputValue}`;
  };

  const _components = {
    MultiValueRemove,
    ClearIndicator: (props: ClearIndicatorProps) => {
      return (
        <components.ClearIndicator
          {...props}
          className="hover:text-danger-base"
        />
      );
    },
    DropdownIndicator: (props: DropdownIndicatorProps) => {
      return (
        <components.DropdownIndicator
          {...props}
          className="hover:text-primary-base"
        />
      );
    },
    Option,
    IndicatorsContainer: (props: IndicatorsContainerProps) => {
      const { isDisabled } = props.selectProps;
      if (isDisabled) return null;
      return (
        <components.IndicatorsContainer
          {...props}
          className={cn(props.className, 'cursor-pointer')}
        />
      );
    },
  };

  const classNames = {
    container: () => 'w-full  text-sm',
    placeholder: () => 'text-[#718FA5]',
    valueContainer: () => 'gap-2',
    multiValue: ({ data }: any) => {
      const isValid = !!value && validator?.(data.value);
      return cn(
        'border',
        'border-[#DCECF7]',
        'group',
        'rounded-[4px]',
        'bg-fill-primary',
        'py-2',
        'font-semibold',
        'px-[5px]',
        'items-center',
        !isValid &&
          'bg-[#e2232320] text-danger-base bg-opacity-30 border-danger-base',
      );
    },
    dropdownIndicator: () => 'hidden',
    input: () => 'cursor-pointer',
    menuList: () =>
      cn(
        'bg-fill-tertiary border py-4 max-w-[400px] rounded-[6px] mt-2 border-separator-primary',
      ),
  };

  return (
    <CreatableSelect
      {...{
        placeholder,
        isDisabled,
        name,
        isMulti: true,
        unstyled: true,
        isClearable,
        allowCreateWhileLoading,
        components: _components,
        onChange: handleChange,
        onBlur,
        options,
        onInputChange,
        noOptionsMessage,
        classNames,
        defaultValue,
        value,
        isLoading,
        onCreateOption,
      }}
    />
  );
};

export default EmailAddressMultiselect;
